import React from 'react';
import { useTheme } from '../theme';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  FormField,
  Avatar,
  Badge,
  Loader,
  MetricCard,
  Select,
  Textarea,
} from '../components';
import { Alert } from '../components/Alert';
import { AgentStatus } from '../components/AgentStatus';
import { CallStatus } from '../components/CallStatus';

export const DesignSystem: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Novofon Design System
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 mb-8">
            A comprehensive design system built with TailwindCSS and React
          </p>
          <Button onClick={toggleTheme} variant="outline">
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>
        </div>

        {/* Colors Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            Color Palette
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Primary Colors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-400 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Primary 400
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-500 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Primary 500
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-600 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Primary 600
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-700 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Primary 700
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-500 rounded border border-gray-200 dark:border-gray-600"></div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Main Primary
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">#45D0BD</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Secondary Colors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary-400 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Secondary 400
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary-500 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Secondary 500
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary-600 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Secondary 600
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary-700 rounded border border-gray-200 dark:border-gray-600"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Secondary 700
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary-500 rounded border border-gray-200 dark:border-gray-600"></div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Main Secondary
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">#8258C8</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Semantic Colors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-success-500 rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">Success</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-warning-500 rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">Warning</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-error-500 rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">Error</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Accent Colors (from HTML designs)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-cyan rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Accent Cyan (#A2F5FF)
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-pink rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Accent Pink (#E987C4)
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-blue rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Accent Blue (#44B6E9)
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-purple rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Accent Purple (#6766C8)
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-darkBlue rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Accent Dark Blue (#2C84C8)
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-neural rounded"></div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Accent Neural (#5F6BC9)
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Typography Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Typography</h2>
          <Card>
            <CardContent className="space-y-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Heading 1</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Display text</p>
              </div>
              <div>
                <h2 className="text-3xl font-semibold text-gray-900 dark:text-white">Heading 2</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Section headers</p>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Heading 3</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Subsection headers</p>
              </div>
              <div>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  Body large text for important content
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Body large</p>
              </div>
              <div>
                <p className="text-base text-gray-700 dark:text-gray-300">
                  Regular body text for most content
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Body regular</p>
              </div>
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-400">
                  Small text for captions and labels
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Body small</p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Buttons Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Buttons</h2>
          <div className="space-y-8">
            {/* Button Variants */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Variants</h3>
              <div className="flex flex-wrap gap-4">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="success">Success</Button>
                <Button variant="gradient">Gradient Brand</Button>
                <Button variant="gradient-purple">Gradient Purple</Button>
                <Button variant="gradient-multi">Gradient Multi</Button>
                <Button variant="gradient-cyan-pink">Gradient Cyan-Pink</Button>
                <Button variant="gradient-brand-cyan">Gradient Brand-Cyan</Button>
                <Button variant="pill-outline">Pill Outline</Button>
                <Button variant="pill-outline-brand">Pill Outline Brand</Button>
                <Button variant="dark">Dark</Button>
                <Button variant="light">Light</Button>
                <Button variant="rounded-outline">Rounded Outline</Button>
                <Button variant="rounded-dark">Rounded Dark</Button>
                <Button variant="rounded-light">Rounded Light</Button>
                <Button variant="link">Link Style</Button>
                <Button variant="icon">Icon</Button>
              </div>
            </div>

            {/* Button Sizes */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Sizes</h3>
              <div className="flex flex-wrap items-center gap-4">
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
                <Button size="xl">Extra Large</Button>
              </div>
            </div>

            {/* Button States */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">States</h3>
              <div className="flex flex-wrap gap-4">
                <Button>Default</Button>
                <Button loading>Loading</Button>
                <Button disabled>Disabled</Button>
              </div>
            </div>

            {/* UI-Kit Specific Buttons */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                UI-Kit Button Styles (from HTML design)
              </h3>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  <Button variant="pill-outline-brand">Get in Touch</Button>
                  <Button variant="gradient-multi">Explore more</Button>
                  <Button variant="gradient-cyan-pink">See demo</Button>
                  <Button variant="gradient-brand-cyan">Get in Touch</Button>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="dark">Sign up</Button>
                  <Button variant="rounded-dark">Add to cart</Button>
                  <Button variant="gradient-multi">Proceed to checkout</Button>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="light">Explore more</Button>
                  <Button variant="rounded-light">View cart</Button>
                  <Button variant="link">Watch video</Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form Components Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            Form Components
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Input Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input label="Email" placeholder="Enter your email" type="email" />
                <Input label="Password" placeholder="Enter your password" type="password" />
                <Input label="Phone" placeholder="Enter your phone number" type="tel" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Form Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField label="Full Name" required>
                  <Input placeholder="Enter your full name" />
                </FormField>
                <FormField label="Company">
                  <Input placeholder="Enter your company name" />
                </FormField>
                <FormField label="Message" error="This field is required">
                  <Input placeholder="Enter your message" />
                </FormField>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Alerts Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Alerts</h2>
          <div className="space-y-4">
            <Alert
              variant="info"
              title="Information"
              description="This is an informational alert message."
            />
            <Alert
              variant="success"
              title="Success!"
              description="Your changes have been saved successfully."
            />
            <Alert
              variant="warning"
              title="Warning"
              description="Please review your information before proceeding."
            />
            <Alert
              variant="error"
              title="Error"
              description="Something went wrong. Please try again."
            />
          </div>
        </section>

        {/* Cards Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Default Card</CardTitle>
                <CardDescription>A basic card with default styling</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300">
                  This is the card content area where you can place any content.
                </p>
              </CardContent>
              <CardFooter>
                <Button variant="outline" size="sm">
                  Action
                </Button>
              </CardFooter>
            </Card>

            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Elevated Card</CardTitle>
                <CardDescription>A card with elevated shadow</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300">
                  This card has a more prominent shadow for emphasis.
                </p>
              </CardContent>
              <CardFooter>
                <Button size="sm">Action</Button>
              </CardFooter>
            </Card>

            <Card variant="outlined">
              <CardHeader>
                <CardTitle>Outlined Card</CardTitle>
                <CardDescription>A card with outlined border</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300">
                  This card uses a border instead of shadow for definition.
                </p>
              </CardContent>
              <CardFooter>
                <Button variant="ghost" size="sm">
                  Action
                </Button>
              </CardFooter>
            </Card>
          </div>
        </section>

        {/* Status Components */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
            Status Components
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Agent Status</CardTitle>
                <CardDescription>Display agent availability status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <AgentStatus status="online" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Online</span>
                </div>
                <div className="flex items-center space-x-4">
                  <AgentStatus status="busy" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Busy</span>
                </div>
                <div className="flex items-center space-x-4">
                  <AgentStatus status="away" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Away</span>
                </div>
                <div className="flex items-center space-x-4">
                  <AgentStatus status="offline" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Offline</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Call Status</CardTitle>
                <CardDescription>Display call connection status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <CallStatus status="connected" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Connected</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CallStatus status="ringing" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Ringing</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CallStatus status="on-hold" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">On Hold</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CallStatus status="ended" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Ended</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Badges</CardTitle>
                <CardDescription>Status indicators and labels</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="default">Default</Badge>
                  <Badge variant="success">Success</Badge>
                  <Badge variant="warning">Warning</Badge>
                  <Badge variant="error">Error</Badge>
                  <Badge variant="info">Info</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge size="sm">Small</Badge>
                  <Badge size="md">Medium</Badge>
                  <Badge size="lg">Large</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Navigation Components - Removed deleted components */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
            Navigation Components
          </h2>
          <Card>
            <CardHeader>
              <CardTitle>Navigation Components</CardTitle>
              <CardDescription>
                Navigation components have been removed from the design system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 dark:text-gray-300">
                Breadcrumb and Pagination components have been removed from the design system.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Form Components */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Form Components</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Select Dropdown</CardTitle>
                <CardDescription>Single selection dropdown</CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  options={[
                    { value: 'option1', label: 'Option 1' },
                    { value: 'option2', label: 'Option 2' },
                    { value: 'option3', label: 'Option 3' },
                  ]}
                  placeholder="Select an option"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Textarea</CardTitle>
                <CardDescription>Multi-line text input</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea placeholder="Enter your message here..." rows={4} />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Data Display Components */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
            Data Display Components
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Avatars</CardTitle>
                <CardDescription>User profile images</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Avatar size="sm" fallback="JD" />
                  <Avatar size="md" fallback="JS" />
                  <Avatar size="lg" fallback="BJ" />
                </div>
                <div className="flex items-center space-x-4">
                  <Avatar
                    size="md"
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face"
                    alt="Alice"
                  />
                  <Avatar
                    size="md"
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face"
                    alt="Bob"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Metric Cards</CardTitle>
                <CardDescription>Key performance indicators</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard title="Total Calls" value="1,234" trend="+12%" trendType="positive" />
                  <MetricCard
                    title="Success Rate"
                    value="94.2%"
                    trend="-2.1%"
                    trendType="negative"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Progress Bars</CardTitle>
                <CardDescription>Progress indicators (component removed)</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300">
                  Progress component has been removed from the design system.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Loading Spinner</CardTitle>
                <CardDescription>Loading indicators</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Loader size="sm" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Small</span>
                </div>
                <div className="flex items-center space-x-4">
                  <Loader size="md" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Medium</span>
                </div>
                <div className="flex items-center space-x-4">
                  <Loader size="lg" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Large</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Interactive Components - Removed deleted components */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
            Interactive Components
          </h2>
          <Card>
            <CardHeader>
              <CardTitle>Interactive Components</CardTitle>
              <CardDescription>
                Interactive components have been removed from the design system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 dark:text-gray-300">
                Tabs, Dropdown, and Tooltip components have been removed from the design system.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Data Table - Removed deleted component */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Data Table</h2>
          <Card>
            <CardHeader>
              <CardTitle>Data Table</CardTitle>
              <CardDescription>
                Table component has been removed from the design system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 dark:text-gray-300">
                Table component has been removed from the design system.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Notifications - Removed deleted component */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Notifications</h2>
          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>
                Notification component has been removed from the design system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 dark:text-gray-300">
                Notification component has been removed from the design system.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Footer */}
        <footer className="text-center py-8 border-t border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-400">
            Novofon Design System - Built with TailwindCSS and React
          </p>
        </footer>
      </div>
    </div>
  );
};
